/*
  <site-header>: keeps the header on screen (always, when scrolling up, or never),
  opens desktop dropdowns on hover or click, and closes them with Escape or a click elsewhere.
  Publishes the header height as --header-height for sticky layouts below it.
*/
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || this;
    this.mode = this.dataset.sticky || 'scroll-up';
    this.section.classList.toggle('header-sticky', this.mode !== 'none');
    this.details = [...this.querySelectorAll('[data-nav-details]')];
    this.lastY = window.scrollY;

    if (this.mode === 'scroll-up') window.addEventListener('scroll', this.onScroll, { passive: true });

    this.details.forEach((details) => {
      details.addEventListener('toggle', this.onToggle);
      const item = details.closest('.nav__item');
      item?.addEventListener('pointerenter', this.onPointerEnter);
      item?.addEventListener('pointerleave', this.onPointerLeave);
    });
    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('keydown', this.onKeydown);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--header-height', `${Math.round(this.getBoundingClientRect().height)}px`);
      });
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.onDocumentClick);
    this.resizeObserver?.disconnect();
  }

  get menuOpen() {
    return this.details.some((details) => details.open);
  }

  onScroll = () => {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      const y = window.scrollY;
      const goingDown = y > this.lastY && y > this.section.offsetHeight + 48;
      this.section.classList.toggle('is-hidden', goingDown && !this.menuOpen);
      this.lastY = y;
      this.ticking = false;
    });
  };

  onToggle = (event) => {
    if (!event.target.open) return;
    this.details.forEach((details) => {
      if (details !== event.target) details.open = false;
    });
  };

  onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    const details = event.currentTarget.querySelector('[data-nav-details]');
    window.clearTimeout(this.closeTimer);
    this.openTimer = window.setTimeout(() => {
      details.open = true;
    }, 120);
  };

  onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    const details = event.currentTarget.querySelector('[data-nav-details]');
    window.clearTimeout(this.openTimer);
    this.closeTimer = window.setTimeout(() => {
      details.open = false;
    }, 220);
  };

  onDocumentClick = (event) => {
    if (!this.menuOpen || this.contains(event.target)) return;
    this.details.forEach((details) => {
      details.open = false;
    });
  };

  onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const open = this.details.find((details) => details.open);
    if (!open) return;
    open.open = false;
    open.querySelector('summary')?.focus();
  };
}

if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
